import { ArrowBack } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import {
  Counterparty,
  CounterpartyCreateUpdate,
  CounterpartyType,
  ExpenseType
} from 'openapi';

import { UnsavedChangesSync } from 'components/shared/UnsavedChangesSync/UnsavedChangesSync';

import { useTranslations } from 'context/TranslationContext';
import { useUnsavedChanges } from 'context/UnsavedChangesContext';

import { counterpartyRequiresExpenseType } from 'utils/helpers/counterPartyApproval';
import {
  validateRegistrationNumber,
  validateVatNumber
} from 'utils/helpers/invoiceHelpers';
import { CounterPartyFormValues } from 'utils/interfaces/CounterParty';
import {
  getCounterPartyPayload,
  mapCounterPartyValues
} from 'utils/mappers/counterParty';

import { actionButtonsContainerStyle } from 'styles/components/Common';
import {
  formWrapperStyle,
  scrollableFormContainerStyle
} from 'styles/pages/ApprovalRuleFormStyle';

import { ApprovalPanel } from './ApprovalPanel';
import { IbanSection } from './IbanSection';
import { SectionCard } from './SectionCard';

interface AddCounterPartyPanelProps {
  readonly headerTitle: string;
  readonly initialValues?: Counterparty;
  readonly expenseTypeOptions: ExpenseType[];
  readonly checkNameExists?: (
    name: string,
    type: CounterpartyType
  ) => Promise<boolean>;
  readonly onClose: () => void;
  readonly onSubmit: (values: CounterpartyCreateUpdate) => void;
}

export const AddCounterPartyPanel = ({
  headerTitle,
  initialValues,
  expenseTypeOptions,
  checkNameExists,
  onClose,
  onSubmit
}: AddCounterPartyPanelProps) => {
  const { translate } = useTranslations();
  const { requestNavigation } = useUnsavedChanges();

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { isDirty }
  } = useForm<CounterPartyFormValues>({
    mode: 'onChange',
    defaultValues: mapCounterPartyValues(initialValues)
  });

  const { append, remove, move } = useFieldArray({
    control,
    name: 'ibans',
    keyName: 'fieldId'
  });

  const [nameExists, setNameExists] = useState(false);
  const [isCheckingName, setIsCheckingName] = useState(false);
  const nameCheckRequestId = useRef(0);

  const watchedName = watch('name');
  const watchedType = watch('type');
  const watchedVatNumber = watch('vatNumber');
  const watchedRegistrationNumber = watch('registrationNumber');
  const watchedExpenseTypeIds = watch('expenseTypeIds') ?? [];
  const watchedIbans = watch('ibans') ?? [];
  const watchedNotes = watch('notes') ?? '';

  useEffect(() => {
    const name = watchedName?.trim();

    if (!checkNameExists || !name) {
      nameCheckRequestId.current += 1;
      setNameExists(false);
      setIsCheckingName(false);
      return;
    }

    const requestId = nameCheckRequestId.current + 1;
    nameCheckRequestId.current = requestId;
    setIsCheckingName(true);

    const timeout = setTimeout(async () => {
      try {
        const exists = await checkNameExists(name, watchedType);

        if (nameCheckRequestId.current === requestId) {
          setNameExists(exists);
        }
      } catch {
        if (nameCheckRequestId.current === requestId) {
          setNameExists(false);
        }
      } finally {
        if (nameCheckRequestId.current === requestId) {
          setIsCheckingName(false);
        }
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [checkNameExists, watchedName, watchedType]);

  const isRegistrationNumberValid = validateRegistrationNumber(
    watchedVatNumber,
    watchedRegistrationNumber
  );
  const isVatNumberValid = validateVatNumber(
    watchedVatNumber,
    watchedRegistrationNumber
  );

  const isCompany = watchedType !== CounterpartyType.REIMBURSEMENT;
  const requiresExpenseType = counterpartyRequiresExpenseType(watchedType);
  const isVatFieldValid =
    !watchedVatNumber?.startsWith('BG') || isVatNumberValid;
  const canSave =
    !!watchedName?.trim() &&
    !nameExists &&
    !isCheckingName &&
    (!requiresExpenseType || watchedExpenseTypeIds.length > 0) &&
    (!isCompany || (isRegistrationNumberValid && isVatFieldValid));

  let sectionNumber = 0;
  const nextSectionNumber = () => {
    sectionNumber += 1;
    return sectionNumber;
  };

  const activeExpenseTypes = expenseTypeOptions.filter(
    (expenseType) => expenseType.isActive
  );

  const handleFormSubmit = (values: CounterPartyFormValues) => {
    onSubmit(getCounterPartyPayload(values));
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(handleFormSubmit)}
      sx={{ ...formWrapperStyle, width: '100%' }}
    >
      <UnsavedChangesSync isDirty={isDirty} />
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          padding: '0.75rem 1rem',
          backgroundColor: '#F5F5F5',
          borderTopLeftRadius: '1rem',
          borderTopRightRadius: '1rem'
        }}
      >
        <Box display="flex" alignItems="center" gap={0.5}>
          <Button
            type="button"
            color="primary"
            startIcon={<ArrowBack />}
            onClick={() => requestNavigation(onClose)}
          >
            {translate('buttons.back')}
          </Button>
          <Typography variant="body2" sx={{ color: '#d1d5db' }}>
            /
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {headerTitle}
          </Typography>
        </Box>
        <Box sx={actionButtonsContainerStyle}>
          <Button type="submit" color="primary" disabled={!canSave}>
            {translate('buttons.save')}
          </Button>
        </Box>
      </Box>

      <Box sx={scrollableFormContainerStyle}>
        <SectionCard
          number={nextSectionNumber()}
          title={translate('labels.counterparty')}
        >
          <Stack spacing={2}>
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <FormControl>
                  <RadioGroup
                    row
                    value={field.value}
                    onChange={(event) => {
                      const nextType = event.target.value as CounterpartyType;
                      field.onChange(nextType);

                      if (nextType === CounterpartyType.REIMBURSEMENT) {
                        setValue('vatNumber', '', { shouldDirty: true });
                        setValue('registrationNumber', '', {
                          shouldDirty: true
                        });
                        setValue('expenseTypeIds', [], { shouldDirty: true });
                      }
                    }}
                  >
                    <FormControlLabel
                      value={CounterpartyType.SUPPLIER}
                      control={<Radio />}
                      label={translate('labels.company')}
                    />
                    <FormControlLabel
                      value={CounterpartyType.REIMBURSEMENT}
                      control={<Radio />}
                      label={translate('labels.reimbursement')}
                    />
                  </RadioGroup>
                </FormControl>
              )}
            />
            <Controller
              name="name"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  required
                  label={translate('labels.name')}
                  placeholder={translate('labels.name')}
                  error={!!error || nameExists}
                  helperText={
                    nameExists
                      ? translate('errors.counterpartyNameExists')
                      : error && translate('errors.counterpartyName')
                  }
                />
              )}
            />
          </Stack>
        </SectionCard>

        {isCompany && (
          <SectionCard
            number={nextSectionNumber()}
            title={translate('addCounterparty.identity')}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Controller
                name="registrationNumber"
                control={control}
                rules={{
                  validate: () =>
                    isRegistrationNumberValid ||
                    translate(
                      !watchedVatNumber || watchedVatNumber.startsWith('BG')
                        ? 'errors.counterpartyRegistrationNumberBG'
                        : 'errors.counterpartyRegistrationNumberForeign'
                    )
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={translate('labels.companyRegistrationNumber')}
                    placeholder={translate('labels.companyRegistrationNumber')}
                    error={!isRegistrationNumberValid}
                    helperText={
                      !isRegistrationNumberValid &&
                      translate(
                        !watchedVatNumber || watchedVatNumber.startsWith('BG')
                          ? 'errors.counterpartyRegistrationNumberBG'
                          : 'errors.counterpartyRegistrationNumberForeign'
                      )
                    }
                  />
                )}
              />
              <Controller
                name="vatNumber"
                control={control}
                rules={{
                  validate: (value) => {
                    if (value?.startsWith('BG')) {
                      return (
                        isVatNumberValid ||
                        translate('errors.counterpartyVatNumberEquality')
                      );
                    }
                    return true;
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={translate('labels.vatNumber')}
                    placeholder={translate('labels.vatNumber')}
                    error={!!field.value && !isVatNumberValid}
                    helperText={
                      field.value &&
                      !isVatNumberValid &&
                      translate('errors.counterpartyVatNumberEquality')
                    }
                  />
                )}
              />
            </Stack>
          </SectionCard>
        )}

        <SectionCard
          number={nextSectionNumber()}
          title={translate('addCounterparty.paymentIbans')}
        >
          <IbanSection
            ibans={watchedIbans}
            onAdd={(iban) => append({ iban, tmpId: uuidv4() })}
            onRemove={remove}
            onMove={move}
          />
        </SectionCard>

        {requiresExpenseType && (
          <SectionCard
            number={nextSectionNumber()}
            title={translate('addCounterparty.expenseTypes')}
          >
            <Controller
              name="expenseTypeIds"
              control={control}
              rules={{
                validate: (value) =>
                  !requiresExpenseType || (value && value.length > 0)
              }}
              render={({ field, fieldState: { error } }) => {
                const selectedExpenseTypes = activeExpenseTypes.filter(
                  (expenseType) =>
                    field.value?.includes(expenseType.id as number)
                );
                return (
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={activeExpenseTypes}
                    value={selectedExpenseTypes}
                    onChange={(_, selected) =>
                      field.onChange(
                        selected.map((expenseType) => expenseType.id)
                      )
                    }
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderValue={(value, getItemProps) =>
                      value.map((option, index) => {
                        const { key, ...itemProps } = getItemProps({ index });
                        return (
                          <Chip
                            key={key}
                            {...itemProps}
                            label={option.name}
                            variant="outlined"
                            sx={{ cursor: 'initial' }}
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={translate('addCounterparty.commonExpenseTypes')}
                        placeholder={translate('addCounterparty.addMore')}
                        error={!!error}
                        helperText={error && translate('errors.expenseType')}
                      />
                    )}
                  />
                );
              }}
            />
          </SectionCard>
        )}

        {isCompany && (
          <SectionCard
            number={nextSectionNumber()}
            title={translate('addCounterparty.groups')}
            action={
              <Button type="button" color="primary" disabled>
                {translate('addCounterparty.manageGroups')}
              </Button>
            }
          >
            <FormControl disabled fullWidth>
              <Select displayEmpty value="" sx={{ textAlign: 'left' }}>
                <MenuItem value="">
                  {translate('addCounterparty.addGroup')}
                </MenuItem>
              </Select>
            </FormControl>
          </SectionCard>
        )}

        <SectionCard
          number={nextSectionNumber()}
          title={translate('addCounterparty.approval')}
        >
          <ApprovalPanel
            name={watchedName ?? ''}
            type={watchedType}
            expenseTypeIds={watchedExpenseTypeIds}
            ibans={watchedIbans}
          />
        </SectionCard>

        <SectionCard
          number={nextSectionNumber()}
          title={translate('addCounterparty.notes')}
        >
          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                multiline
                minRows={4}
                fullWidth
                placeholder={translate('addCounterparty.notesPlaceholder')}
                slotProps={{ htmlInput: { maxLength: 500 } }}
                helperText={`${watchedNotes.length}/500`}
              />
            )}
          />
        </SectionCard>
      </Box>
    </Box>
  );
};
